import { Icon } from '@iconify/react';
import { Box, Stack, Tooltip, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ReactLoading from 'react-loading';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Switch } from '../../../components/custom/Switch';
import { formatDate, formatMoney } from '../../../utils/format/format';
import { useAuth } from '../../../utils/context/useAuthContext';
import { UserRole } from '../../../utils/types/types';
import Employees from './Employees/Employees';
import Payments from './Payments/Payments';

import { SFNClient, StartExecutionCommand } from '@aws-sdk/client-sfn';
import { generateClient } from 'aws-amplify/api';
import { useSnackbar } from 'notistack';
import { Schema } from '../../../../amplify/data/resource';
import awsConfig from '../../../amplify_outputs.json';
import SendButton from './SendButton';
import Button from '../../../components/custom/Button';

// @ts-ignore
type Employee = Schema['Employee']['type'];

const apiClient = generateClient<Schema>();

const STEP_FUNCTION_ARN = awsConfig.custom.processFileStepFunctionArn;

const File = () => {
  const theme = useTheme();
  const [mode, setMode] = useState('PAYMENTS');
  const navigate = useNavigate();
  const { user } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const [searchParams, setSearchParams] = useSearchParams();
  const fileId = searchParams.get('fileId');

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMode(event.target.checked ? 'PAYMENTS' : 'EMPLOYEES');
  };

  const [lastUpdated, setLastUpdated] = useState<number>(Date.now());

  const getFileIcon = (status: String) => {
    if (status === 'COMPLETED') {
      return '/assets/dashboard/fileGreen.svg';
    } else if (status === 'IN_PROGRESS') {
      return '/assets/dashboard/fileYellow.svg';
    } else if (status === 'PAID') {
      return '/assets/dashboard/fileYellow.svg';
    } else if (status === 'PENDING') {
      return '/assets/dashboard/fileGrey.svg';
    } else if (status === 'REJECTED') {
      return '/assets/dashboard/fileRed.svg';
    }
  };

  const [file, setFile] = useState<any>(null);
  const [lastFile, setLastFile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [lastFileLoading, setLastFileLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  useEffect(() => {
    const fetchFile = async () => {
      if (!fileId) return;

      if (!file) {
        setLoading(true);
      }

      try {
        const response = await apiClient.models.File.get(
          { id: fileId },
          {
            selectionSet: [
              'id',
              'path',
              'fileName',
              'date',
              'paymentDate',
              'status',
              'error',
              'type',
              'xmlVersion',
              'executionDate',
              'workEnvironment',
              'fileNumber',
              'sequenceNumber',
              'senderCode',
              'salaryMonth',
              'senderIdentifierType',
              'senderId',
              'senderName',
              'senderContactFirstName',
              'senderContactLastName',
              'senderContactPhone',
              'senderContactEmail',
              'senderContactCellular',
              'recipientCode',
              'recipientIdentifierType',
              'recipientId',
              'recipientCreatorIdentifier',
              'userId',
              'user.*',
              'deposits.*',
              'deposits.employees.*',
              'deposits.employees.fundContributions.*'
            ],
          },
        );

        if (response?.data) {
          console.log('File data:', response.data);
          setFile(response.data);
        } else {
          console.warn('Response data is null or undefined for main file.');
        }
      } catch (error) {
        console.error('Error fetching file:', error);
      } finally {
        // Only set loading false if dialog is not open
        if (!dialogOpen) {
          setLoading(false);
        }
      }
    };

    if (fileId) {
      fetchFile();
    }

    let updateSubscription: { unsubscribe: () => void } | null = null;

    if (fileId) {
      updateSubscription = apiClient.models.File.onUpdate({
        filter: { id: { eq: fileId } },
      }).subscribe({
        next: (updatedData: any) => {
          console.log('File update received, dialogOpen:', dialogOpen);
          
          // If dialog is open and we're waiting for payment link, don't re-fetch
          if (dialogOpen) {
            console.log('Dialog is open, skipping full re-fetch');
            // Only update critical fields without causing re-render
            if (updatedData.paymentLink && !file?.paymentLink) {
              setFile((prevFile: any) => ({
                ...prevFile,
                paymentLink: updatedData.paymentLink,
                status: updatedData.status
              }));
            }
          } else {
            // Normal update flow when dialog is not open
            fetchFile();
          }
        },
        error: (error: any) =>
          console.warn(`Subscription error on file ${fileId}:`, error),
      });
    }

    return () => {
      if (updateSubscription) {
        console.log(`Unsubscribing from file updates: ${fileId}`);
        updateSubscription.unsubscribe();
      }
    };
  }, [fileId, lastUpdated, dialogOpen, file?.paymentLink]);

  const totalToBePaid = Array.isArray(file?.deposits)
    ? file.deposits.reduce(
        (accumulator: number, deposit: { totalDepositAmount?: number }) => {
          return accumulator + (deposit.totalDepositAmount || 0);
        },
        0,
      )
    : 0;

  const employees: Employee[] = useMemo(() => {
    return Array.isArray(file?.deposits)
      ? file.deposits
          .flatMap((deposit: any) => deposit.employees || [])
          .reduce((employees: Employee[], current: Employee) => {
            const existingEmployeeIndex = employees.findIndex(
              (emp) => emp.employeeId === current.employeeId,
            );

            if (existingEmployeeIndex >= 0) {
              const existingEmployee = employees[existingEmployeeIndex];

              const updatedEmployee = {
                ...existingEmployee,
                employeeContributionAllocation: [
                  ...(existingEmployee.employeeContributionAllocation || []),
                  ...(current.employeeContributionAllocation || []),
                ],
              };

              employees[existingEmployeeIndex] = updatedEmployee;
            } else {
              employees.push(current);
            }
            return employees;
          }, [])
      : [];
  }, [file]);

  // Общее количество записей сотрудников из всех депозитов
  const totalEmployeeRecords = useMemo(() => {
    return Array.isArray(file?.deposits)
      ? file.deposits.reduce((total: number, deposit: any) => {
          return total + (deposit.employees?.length || 0);
        }, 0)
      : 0;
  }, [file]);

  // Количество уникальных employeeId
  const uniqueEmployees = employees.length;

  // Количество уникальных employeeId для комиссии (с учетом месяцев и contributionAmount)
  const uniqueEmployeesForFees = useMemo(() => {
    if (!Array.isArray(file?.deposits)) return 0;
    
    let totalCount = 0;
    
    // Проходим по всем сотрудникам
    employees.forEach((employee) => {
      // Собираем все fundContributions для этого сотрудника
      const allFundContributions: any[] = [];
      
      file.deposits.forEach((deposit: any) => {
        if (Array.isArray(deposit.employees)) {
          deposit.employees.forEach((emp: any) => {
            if (emp.employeeId === employee.employeeId && Array.isArray(emp.fundContributions)) {
              allFundContributions.push(...emp.fundContributions);
            }
          });
        }
      });
      
      // Проверяем есть ли хотя бы один fundContribution с contributionAmount > 0
      const hasValidContributions = allFundContributions.some(
        (contribution: any) => (contribution.contributionAmount || 0) > 0
      );
      
      if (hasValidContributions) {
        // Собираем уникальные salaryMonth для этого сотрудника
        const uniqueMonths = new Set(
          allFundContributions
            .filter((contribution: any) => (contribution.contributionAmount || 0) > 0)
            .map((contribution: any) => contribution.salaryMonth)
            .filter(Boolean) // Исключаем null/undefined
        );
        
        // Добавляем количество уникальных месяцев к общему счету
        totalCount += uniqueMonths.size;
      }
    });
    
    return totalCount;
  }, [file, employees]);

  const deposits = file?.deposits;

  const handleDownload = async () => {
    if (file) {
      const bucketName = awsConfig.storage.bucket_name;
      const fileKey = `files/${file.id}`;

      try {
        const downloadResponse = await apiClient.queries.downloadFile({
          path: fileKey,
          bucketName: bucketName,
        });

        if (downloadResponse?.data) {
          // Parse the JSON response
          const responseData = typeof downloadResponse.data === 'string' 
            ? JSON.parse(downloadResponse.data) 
            : downloadResponse.data;

          if (responseData?.body) {
            // Convert base64 to blob
            const base64Data = responseData.body;
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { 
              type: responseData.headers?.['Content-Type'] || 'application/octet-stream' 
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.fileName || responseData.metadata?.fileName || 'file';
            a.click();
            window.URL.revokeObjectURL(url);

            enqueueSnackbar('הקובץ ירד בהצלחה!', { variant: 'success' });
          } else {
            throw new Error('לא התקבל תוכן קובץ');
          }
        } else {
          throw new Error('לא התקבל תוכן קובץ');
        }
      } catch (error) {
        console.error('Error downloading file:', error);
        enqueueSnackbar('שגיאה בהורדת הקובץ', { variant: 'error' });
      }
    } else {
      enqueueSnackbar('לא ניתן להוריד את הקובץ', { variant: 'error' });
    }
  };

  const restartFile = async (fileId: string) => {
    if (!fileId) {
      console.error('File ID is missing');
      return;
    }

    try {
      const updateFileResponse = await apiClient.models.File.update({
        id: fileId,
        status: 'PROCESSING',
      });

      if (!updateFileResponse || !updateFileResponse.data) {
        console.error('Failed to update file status.');
        return;
      }

      const triggerProcessFile = await apiClient.queries.triggerProcessFile({
        fileId,
        replace: true,
        arn: STEP_FUNCTION_ARN,
      });

      console.log('Trigger Process File:', triggerProcessFile);

      setLoading(true);

      setLastUpdated(Date.now());
    } catch (error) {
      console.error('Error reprocessing file:', error);
    }
  };

  const handleDeleteFile = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDeleteFile = async () => {
    try {
      await apiClient.models.File.delete({
        id: file.id
      });
      enqueueSnackbar('הקובץ נמחק בהצלחה', { variant: 'success' });
      setOpenDeleteDialog(false);
      navigate('/clients'); // Navigate back to clients list after deletion
    } catch (error) {
      enqueueSnackbar('שגיאה במחיקת הקובץ', { variant: 'error' });
      console.error('Error deleting file:', error);
    }
  };



  if (file?.status === 'PROCESSING' || !file) {
    return (
      <Box sx={{ mt: '250px', display: 'flex', justifyContent: 'center' }}>
        <ReactLoading
          type={'bubbles'}
          color={theme.palette.green.main}
          height={250}
          width={250}
        />
      </Box>
    );
  }
  if (file) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '52px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              navigate(-1);
            }}
          >
            <Icon icon="lets-icons:refund-back" />
            <Typography>חזרה</Typography>
          </Stack>
          <Typography
            fontWeight={'300'}
            fontSize={'30px'}
            fontFamily={'AlmoniDL'}
          ></Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '15px',
            }}
          >
            <SendButton
              status={file.status}
              fileId={file.id}
              amount={totalToBePaid}
              employees={totalEmployeeRecords}
              deposits={deposits.length}
              depositsArray={deposits}
              uniqueEmployees={uniqueEmployees}
              uniqueEmployeesForFees={uniqueEmployeesForFees}
              salaryMonth={file?.salaryMonth || ''}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#DAE2EA',
                px: '30px',
                py: '40px',
                flexDirection: 'column',
                borderRadius: '30px',
                gap: '10px',
              }}
            >
              <img alt="card" src="/assets/file/card.svg" />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography
                  fontWeight={'500'}
                  fontSize={'35px'}
                  fontFamily={'Montserrat'}
                >
                  {uniqueEmployees.toString().padStart(2, '0')}
                </Typography>

                <Typography
                  fontWeight={'700'}
                  fontSize={'16px'}
                  fontFamily={'AlmoniDL'}
                >
                  עובדים
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#343434',
                p: '30px',
                borderRadius: '30px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '10px',
                  ml: 'auto',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                >
                  <Typography
                    fontWeight={'500'}
                    fontSize={'35px'}
                    fontFamily={'Montserrat'}
                    textAlign={'right'}
                    color={'#C1FE40'}
                  >
                    {formatMoney(totalToBePaid)}
                  </Typography>
                  <Typography
                    fontWeight={'700'}
                    fontSize={'16px'}
                    fontFamily={'AlmoniDL'}
                    textAlign={'right'}
                    color={'#C1FE40'}
                  >
                    סהכ הפקדה
                  </Typography>
                </Box>
                <img
                  alt="coin"
                  src="/assets/file/coin.svg"
                  style={{ alignSelf: 'flex-end' }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'space-between',
                flex: 1,
                flexDirection: 'row-reverse',
                backgroundColor: '#FDFDFD80',
                borderRadius: '30px',
                p: '20px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  background: '#FFFFFF',
                  p: '28px',
                  borderRadius: '20px',
                }}
              >
                <img alt="file" src={getFileIcon(file.status)} />
                <Typography
                  fontWeight={'500'}
                  fontSize={'35px'}
                  fontFamily={'Montserrat'}
                  textAlign={'right'}
                  color={'#808080'}
                >
                  {deposits.length.toString().padStart(2, '0')}
                </Typography>
                <Typography
                  fontWeight={'700'}
                  fontSize={'16px'}
                  fontFamily={'AlmoniDL'}
                  textAlign={'right'}
                  color={'#808080'}
                >
                  הוראות תשלום
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Stack sx={{ width: '100%', pt: '70px' }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        fontWeight={'700'}
                        fontSize={'16px'}
                        fontFamily={'AlmoniDL'}
                        textAlign={'right'}
                      >
                        שכר חודשי
                      </Typography>
                      <Typography
                        fontWeight={'700'}
                        fontSize={'26px'}
                        fontFamily={'AlmoniDL'}
                        textAlign={'right'}
                      >
                        {formatDate(file.date)}
                      </Typography>
                    </Box>
                    <Typography
                      fontWeight={'700'}
                      fontSize={'18px'}
                      fontFamily={'AlmoniDL'}
                      textAlign={'right'}
                    >
                      {file?.senderName}
                    </Typography>
                  </Stack>
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {user?.role === UserRole.ADMIN && (
                      <Tooltip title="מחק קובץ">
                        <Box
                          sx={{
                            backgroundColor: '#FFF',
                            borderRadius: '10px',
                            p: '12px',
                            pb: '10px',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#ffebee',
                            }
                          }}
                          onClick={handleDeleteFile}
                        >
                          <Icon icon="mdi:delete" width={20} height={20} color="red" />
                        </Box>
                      </Tooltip>
                    )}
                    <Tooltip title="Download">
                      <img
                        src="/assets/file/download.svg"
                        style={{ cursor: 'pointer' }}
                        onClick={handleDownload}
                      />
                    </Tooltip>
                  </Stack>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        fontWeight={mode === 'EMPLOYEES' ? '700' : '300'}
                        fontSize={'14px'}
                        fontFamily={'AlmoniDL'}
                      >
                        עובדים
                      </Typography>
                      <Switch
                        checked={mode === 'PAYMENTS'}
                        onChange={handleSwitchChange}
                      />

                      <Typography
                        fontWeight={mode === 'PAYMENTS' ? '700' : '300'}
                        fontSize={'14px'}
                        fontFamily={'AlmoniDL'}
                      >
                        הוראות תשלום
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {mode === 'EMPLOYEES' ? (
          <Employees employees={employees} date={file.date} />
        ) : (
          <Payments deposits={deposits} />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: '12px',
              minWidth: '400px',
              textAlign: 'center',
            },
          }}
        >
          <DialogTitle sx={{ 
            fontFamily: 'AlmoniDL', 
            fontSize: '20px', 
            fontWeight: 700,
            textAlign: 'center',
            pb: 1,
            color: 'red'
          }}>
            מחיקת קובץ
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pt: 1 }}>
            <Typography dir='rtl' sx={{ 
              fontFamily: 'AlmoniDL', 
              fontSize: '16px',
              fontWeight: 700,
              color: 'black'
            }}>
              האם אתה בטוח שברצונך למחוק את הקובץ?
            </Typography>
            <Typography dir='rtl' sx={{ 
              fontFamily: 'AlmoniDL', 
              fontSize: '14px',
              fontWeight: 500,
              color: 'red',
              mt: 1
            }}>
              פעולה זו לא ניתנת לביטול
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
            <Button
              onClick={handleConfirmDeleteFile}
              customVariant="default"
              sx={{
                fontFamily: 'AlmoniDL',
                fontWeight: 700,
                borderRadius: '8px',
                px: 3,
                backgroundColor: 'red',
                '&:hover': {
                  backgroundColor: 'darkred',
                }
              }}
            >
              מחק
            </Button>
            <Button
              onClick={handleCloseDeleteDialog}
              customVariant="light"
              sx={{
                fontFamily: 'AlmoniDL',
                fontWeight: 700,
                borderRadius: '8px',
                px: 3,
              }}
            >
              ביטול
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  } else {
    return <Box></Box>;
  }
};

export default File;
